import { useEffect, useState, type ReactNode } from 'react';
import { useAuthStore } from '../../stores/auth';
import { SkeletonTable, SkeletonText } from '../../components/Skeleton';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.profitos.pro';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
  createdAt: string;
  userCount: number;
}

interface TenantsResponse {
  success: boolean;
  data?: { tenants: Tenant[] };
  error?: { message?: string };
}

export function AdminTenantsPage(): ReactNode {
  const { accessToken } = useAuthStore();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchTenants(): Promise<void> {
      try {
        const res = await fetch(`${API_URL}/admin/tenants`, {
          headers: {
            Authorization: `Bearer ${accessToken ?? ''}`,
          },
        });

        if (!res.ok) {
          throw new Error('Failed to fetch tenants');
        }

        const data = (await res.json()) as TenantsResponse;
        if (data.success && data.data !== undefined) {
          setTenants(data.data.tenants);
        } else {
          throw new Error(data.error?.message ?? 'Failed to fetch tenants');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load tenants');
      } finally {
        setLoading(false);
      }
    }

    void fetchTenants();
  }, [accessToken]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <SkeletonText width="w-32" size="lg" />
          <div className="mt-2">
            <SkeletonText width="w-48" size="sm" />
          </div>
        </div>
        <SkeletonTable rows={5} columns={5} />
      </div>
    );
  }

  if (error !== null) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Tenants</h1>
        <span className="text-sm text-gray-500">{tenants.length} total</span>
      </div>

      <div className="rounded-lg bg-white shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tenant
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Plan
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Users
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tenants.map((tenant) => (
              <tr key={tenant.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{tenant.name}</div>
                  <div className="text-sm text-gray-500">{tenant.slug}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-800">
                    {tenant.plan}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                      tenant.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {tenant.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {tenant.userCount}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(tenant.createdAt).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {tenants.length === 0 && (
          <div className="text-center py-12 text-gray-500">No tenants found</div>
        )}
      </div>
    </div>
  );
}
